import {defineStore} from 'pinia';
import {ref} from 'vue';
import type {RouteLocationRaw} from 'vue-router';

/** How long a snackbar stays up when the caller does not say otherwise (design system: 8s). */
export const SNACKBAR_DEFAULT_DURATION_MS = 8000;

/** How long a toast stays up (design system: 4s). */
export const TOAST_DURATION_MS = 4000;

/** The way somewhere the snackbar offers, drawn as a text action. */
export interface SnackbarAction {
  label: string;
  to: RouteLocationRaw;
}

/** What a caller asks the snackbar to show. */
export interface SnackbarOptions {
  /** Already translated: the snackbar shows what it is given. */
  message: string;
  action?: SnackbarAction;
  /** Whether a "Dismiss" text action sits beside the action, taking the snackbar down at once. */
  dismissible?: boolean;
  /** Milliseconds of no interaction before it takes itself down. */
  durationMs?: number;
}

/** The snackbar that is up, with every default filled in. */
export interface ShownSnackbar {
  /** Tells one showing from the next, even when both carry the same message. */
  id: number;
  message: string;
  action: SnackbarAction | null;
  dismissible: boolean;
  durationMs: number;
}

/** The toast that is up. */
export interface ShownToast {
  /** Tells one showing from the next, even when both carry the same message. */
  id: number;
  message: string;
}

export const useSnackbarStore =
  /**
   * The design system's two kinds of feedback: the toast confirms something with nothing to do about
   * it, the snackbar confirms something and offers what to do next. Raised from wherever the action
   * happened.
   *
   * One at a time across both kinds: the newest replaces whatever was up. On a phone the two sit
   * on the same strip at the foot of the screen, so a second one could only cover the first, and the
   * reader is told only about what happened last.
   */
  defineStore('snackbar-store', () => {
  const current = ref<ShownSnackbar | null>(null);
  const currentToast = ref<ShownToast | null>(null);
  let lastId = 0;

  /** Raises a snackbar in place of whatever was up, with its countdown starting afresh. */
  const show = (options: SnackbarOptions): void => {
    lastId++;
    currentToast.value = null;
    current.value = {
      id: lastId,
      message: options.message,
      action: options.action ?? null,
      dismissible: options.dismissible ?? false,
      durationMs: options.durationMs ?? SNACKBAR_DEFAULT_DURATION_MS,
    };
  };

  /**
   * Takes the snackbar down, only if the one given is still the one up: a countdown or a click that
   * belongs to a snackbar already replaced must not take its successor with it.
   */
  const hide = (id: number): void => {
    if (current.value?.id === id) {
      current.value = null;
    }
  };

  /** Raises a toast in place of whatever was up. Already translated. */
  const showToast = (message: string): void => {
    lastId++;
    current.value = null;
    currentToast.value = {id: lastId, message};
  };

  /** Takes the toast down, only if the one given is still the one up. */
  const hideToast = (id: number): void => {
    if (currentToast.value?.id === id) {
      currentToast.value = null;
    }
  };

  return {
    current,
    currentToast,
    show,
    hide,
    showToast,
    hideToast,
  };
});
